import React, { useEffect, useState } from 'react';
import { ArrowRightCircle, Edit, Plus, RefreshCw, Trash2, Search, Filter, Users, CheckCircle, AlertCircle, TrendingUp, Calendar, ChevronLeft, ChevronRight, X, UserPlus, Shield, Info } from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

const SuccessionPlanning = () => {
  const { user } = useAuth();
  const { filialeId, currentFiliale } = useFiliale();
  const [plannings, setPlannings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [dropdownData, setDropdownData] = useState({
    personnel: [],
    titres: []
  });
  const [formData, setFormData] = useState({
    titre_id: '',
    filiale_id: filialeId || '',
    successeur_1_id: '',
    successeur_2_id: '',
    successeur_3_id: '',
    preparation_successeur_1: '',
    preparation_successeur_2: '',
    preparation_successeur_3: '',
    date_identification: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState(1);

  useEffect(() => {
    fetchPlannings();
    fetchDropdownData();
  }, [filialeId]);

  const fetchPlannings = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filialeId && filialeId !== 'all') {
        params.filiale_id = filialeId;
      }
      
      const queryString = new URLSearchParams(params).toString();
      const response = await http.get(`/api/succession-planning${queryString ? '?' + queryString : ''}`);
      setPlannings(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement succession planning:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const currentFilialeId = filialeId;
      const personnelUrl = (currentFilialeId && currentFilialeId !== 'all') 
        ? `/api/personnel?filiale_id=${currentFilialeId}` 
        : '/api/personnel';
      
      const [personnel, titres] = await Promise.all([
        http.get(personnelUrl),
        http.get('/api/titres', { params: currentFilialeId && currentFilialeId !== 'all' ? { filiale_id: currentFilialeId } : {} })
      ]);

      const personnelArray = Array.isArray(personnel.data) ? personnel.data : 
                             (personnel.data && personnel.data.data && Array.isArray(personnel.data.data) ? personnel.data.data : []);

      const titresArray = Array.isArray(titres.data) ? titres.data : 
                        (titres.data && titres.data.data && Array.isArray(titres.data.data) ? titres.data.data : []);

      setDropdownData({
        personnel: personnelArray,
        titres: titresArray
      });
    } catch (error) {
      console.error('Erreur chargement dropdown:', error);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setActiveTab(1);
    setFormData({
      titre_id: '',
      filiale_id: filialeId || '',
      successeur_1_id: '',
      successeur_2_id: '',
      successeur_3_id: '',
      preparation_successeur_1: '',
      preparation_successeur_2: '',
      preparation_successeur_3: '',
      date_identification: ''
    });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setActiveTab(1);
    setFormData({ ...item });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce succession planning ?')) {
      return;
    }
    try {
      await http.delete(`/api/succession-planning/${id}`);
      fetchPlannings();
    } catch (error) {
      console.error('Erreur suppression succession planning:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await http.put(`/api/succession-planning/${editingItem.id}`, formData);
      } else {
        await http.post('/api/succession-planning', formData);
      }
      setShowModal(false);
      fetchPlannings();
    } catch (error) {
      console.error('Erreur sauvegarde succession planning:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  // Filtrage et pagination
  const filteredPlannings = plannings.filter(p => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (p.position_nom && p.position_nom.toLowerCase().includes(searchLower)) ||
      (p.successeur_1_nom && p.successeur_1_nom.toLowerCase().includes(searchLower)) ||
      (p.successeur_2_nom && p.successeur_2_nom.toLowerCase().includes(searchLower))
    );
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPlannings = filteredPlannings.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPlannings.length / itemsPerPage);

  const getPreparationColor = (preparation) => {
    const colors = {
      'pret': 'bg-green-100 text-green-800',
      'presque_pret': 'bg-blue-100 text-blue-800',
      'en_developpement': 'bg-yellow-100 text-yellow-800',
      'non_pret': 'bg-red-100 text-red-800'
    };
    return colors[preparation] || 'bg-gray-100 text-gray-800';
  };

  const getPreparationIcon = (preparation) => {
    const icons = {
      'pret': <CheckCircle className="h-4 w-4" />,
      'presque_pret': <TrendingUp className="h-4 w-4" />,
      'en_developpement': <AlertCircle className="h-4 w-4" />,
      'non_pret': <AlertCircle className="h-4 w-4" />
    };
    return icons[preparation] || <AlertCircle className="h-4 w-4" />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Succession Planning"
        description="Gestion des successeurs et relève de poste"
        icon={Users}
        actions={[
          {
            label: 'Nouveau planning',
            icon: Plus,
            onClick: handleCreate,
            primary: true
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: fetchPlannings,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8">
        {/* Recherche */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-2">
            <Search className="h-5 w-5 text-gray-500" />
            <input
              type="text"
              placeholder="Rechercher une position ou un successeur..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>

        {/* Tableau des succession plannings */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex h-48 items-center justify-center text-sm text-gray-500">
              Chargement...
            </div>
          ) : currentPlannings.length === 0 ? (
            <div className="px-4 py-16 text-center text-sm text-gray-500">
              Aucun succession planning trouvé.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Position</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Successeur 1</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Préparation</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Successeur 2</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Préparation</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Successeur 3</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Préparation</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date identification</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentPlannings.map((planning) => (
                      <tr key={planning.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {planning.titre_nom_developpe || planning.titre_nom || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {planning.successeur_1_nom ? `${planning.successeur_1_prenom} ${planning.successeur_1_nom}` : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {planning.preparation_successeur_1 ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getPreparationColor(planning.preparation_successeur_1)}`}>
                              {getPreparationIcon(planning.preparation_successeur_1)}
                              {planning.preparation_successeur_1.replace(/_/g, ' ')}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {planning.successeur_2_nom ? `${planning.successeur_2_prenom} ${planning.successeur_2_nom}` : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {planning.preparation_successeur_2 ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getPreparationColor(planning.preparation_successeur_2)}`}>
                              {getPreparationIcon(planning.preparation_successeur_2)}
                              {planning.preparation_successeur_2.replace(/_/g, ' ')}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {planning.successeur_3_nom ? `${planning.successeur_3_prenom} ${planning.successeur_3_nom}` : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {planning.preparation_successeur_3 ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getPreparationColor(planning.preparation_successeur_3)}`}>
                              {getPreparationIcon(planning.preparation_successeur_3)}
                              {planning.preparation_successeur_3.replace(/_/g, ' ')}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {planning.date_identification ? new Date(planning.date_identification).toLocaleDateString('fr-FR') : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(planning)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(planning.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredPlannings.length)} sur {filteredPlannings.length} plannings
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Précédent
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 border rounded ${
                        currentPage === page
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Modal Succession Planning */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="w-full max-w-4xl rounded-xl bg-white shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b bg-gray-50">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">
                    {editingItem ? 'Modifier' : 'Créer'} Succession Planning
                  </h3>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-gray-500 hover:text-gray-900"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Onglets */}
              <div className="border-b bg-white">
                <div className="flex">
                  <button
                    key="tab-1"
                    onClick={() => setActiveTab(1)}
                    className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                      activeTab === 1
                        ? 'border-blue-600 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Position
                  </button>
                  <button
                    key="tab-2"
                    onClick={() => setActiveTab(2)}
                    className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                      activeTab === 2
                        ? 'border-blue-600 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Successeurs
                  </button>
                </div>
              </div>

              {/* Contenu */}
              <div className="flex-1 overflow-y-auto p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Onglet 1: Position */}
                  {activeTab === 1 && (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                        <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-blue-800">
                          <p className="font-semibold mb-1">Position clé</p>
                          <p>Définissez la position stratégique pour laquelle vous identifierez des successeurs. Cette position doit être critique pour l'entreprise.</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                            <Shield className="h-4 w-4 text-blue-600" />
                            Poste (Titre) *
                          </label>
                          <select
                            value={formData.titre_id}
                            onChange={(e) => setFormData({ ...formData, titre_id: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          >
                            <option value="">Sélectionner</option>
                            {dropdownData.titres.map(t => (
                              <option key={t.idtitre} value={t.idtitre}>{t.nom_developpe || t.nom}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">ID Position</label>
                          <input
                            type="text"
                            value={formData.titre_id}
                            onChange={(e) => setFormData({ ...formData, titre_id: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="ID du titre"
                            disabled
                          />
                        </div>
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                            <Calendar className="h-4 w-4 text-purple-600" />
                            Date d'identification
                          </label>
                          <input
                            type="date"
                            value={formData.date_identification}
                            onChange={(e) => setFormData({ ...formData, date_identification: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Onglet 2: Successeurs */}
                  {activeTab === 2 && (
                    <div className="space-y-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                        <Info className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-green-800">
                          <p className="font-semibold mb-1">Successeurs potentiels</p>
                          <p>Identifiez jusqu'à 3 successeurs potentiels pour cette position et évaluez leur niveau de préparation. Cela assure la continuité en cas de départ ou de promotion.</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                              <UserPlus className="h-4 w-4 text-green-600" />
                              Successeur 1
                            </label>
                            <select
                              value={formData.successeur_1_id}
                              onChange={(e) => setFormData({ ...formData, successeur_1_id: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                              <option value="">Sélectionner</option>
                              {dropdownData.personnel.map(p => (
                                <option key={p.idpersonnel} value={p.idpersonnel}>{p.prenom} {p.nom}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Niveau de préparation</label>
                            <select
                              value={formData.preparation_successeur_1}
                              onChange={(e) => setFormData({ ...formData, preparation_successeur_1: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                              <option value="">Sélectionner</option>
                              <option value="pret">Prêt - Peut prendre le poste immédiatement</option>
                              <option value="presque_pret">Presque prêt - Quelques mois de formation</option>
                              <option value="en_developpement">En développement - Plan de développement requis</option>
                              <option value="non_pret">Non prêt - Pas encore éligible</option>
                            </select>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                              <UserPlus className="h-4 w-4 text-green-600" />
                              Successeur 2
                            </label>
                            <select
                              value={formData.successeur_2_id}
                              onChange={(e) => setFormData({ ...formData, successeur_2_id: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                              <option value="">Sélectionner</option>
                              {dropdownData.personnel.map(p => (
                                <option key={p.idpersonnel} value={p.idpersonnel}>{p.prenom} {p.nom}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Niveau de préparation</label>
                            <select
                              value={formData.preparation_successeur_2}
                              onChange={(e) => setFormData({ ...formData, preparation_successeur_2: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                              <option value="">Sélectionner</option>
                              <option value="pret">Prêt - Peut prendre le poste immédiatement</option>
                              <option value="presque_pret">Presque prêt - Quelques mois de formation</option>
                              <option value="en_developpement">En développement - Plan de développement requis</option>
                              <option value="non_pret">Non prêt - Pas encore éligible</option>
                            </select>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                              <UserPlus className="h-4 w-4 text-green-600" />
                              Successeur 3
                            </label>
                            <select
                              value={formData.successeur_3_id}
                              onChange={(e) => setFormData({ ...formData, successeur_3_id: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                              <option value="">Sélectionner</option>
                              {dropdownData.personnel.map(p => (
                                <option key={p.idpersonnel} value={p.idpersonnel}>{p.prenom} {p.nom}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Niveau de préparation</label>
                            <select
                              value={formData.preparation_successeur_3}
                              onChange={(e) => setFormData({ ...formData, preparation_successeur_3: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                              <option value="">Sélectionner</option>
                              <option value="pret">Prêt - Peut prendre le poste immédiatement</option>
                              <option value="presque_pret">Presque prêt - Quelques mois de formation</option>
                              <option value="en_developpement">En développement - Plan de développement requis</option>
                              <option value="non_pret">Non prêt - Pas encore éligible</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveTab(Math.max(1, activeTab - 1))}
                    disabled={activeTab === 1}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Précédent
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab(Math.min(2, activeTab + 1))}
                    disabled={activeTab === 2}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    Suivant
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingItem ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuccessionPlanning;
